import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getRoutineByDay } from "../../redux/actions";
import Topbar from "../global/Topbar";
import { tokens } from "../../theme";

const RoutineManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { day } = useParams();
  const routines = useSelector((state) => state.Routine);

  useEffect(() => {
    if (day) dispatch(getRoutineByDay(day));
  }, [dispatch, day]);

  const renderSingleCard = (exerciseData) => {
    const { exercise, reps, sets, exerciseId, description, position } = exerciseData;

    return (
      <Grid item xs={12} sm={12} md={4} key={exerciseId}>
        <Card
          sx={{
            height: "100%",
            backgroundColor: colors.grey[800],
            color: "#fff",
            cursor: "pointer",
            borderRadius: 3,
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.03)",
              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
            },
          }}
          onClick={() => navigate(`/routines/${day}/${exerciseId}`)}
        >
          <Box sx={{ position: "relative" }}>
            {exercise?.image ? (
              <CardMedia
                component="img"
                height="160"
                image={exercise.image}
                alt={exercise.name}
              />
            ) : (
              <Box
                sx={{
                  height: 160,
                  background: "linear-gradient(135deg, #0f172a, #1e293b)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" color="white">
                  {exercise?.name}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: colors.primary[700],
                borderRadius: "8px",
                padding: "2px 8px",
                fontSize: "0.8rem",
              }}
            >
              #{position}
            </Box>
          </Box>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {exercise?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300] }}>
              {exercise?.description}
            </Typography>
            <Typography variant="body2" mt={1}>
              <strong>Series:</strong> {sets} &nbsp;&nbsp; <strong>Reps:</strong> {reps}
            </Typography>
            {description && (
              <Typography variant="body2" mt={1} sx={{ fontStyle: "italic" }}>
                {description}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderSupersetCard = (supersetData, index) => {
    return (
      <Grid item xs={12} key={`superset-${index}`}>
        <Card
          sx={{
            backgroundColor: colors.primary[700],
            color: "#fff",
            borderRadius: 3,
            boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            🔗 Superset - #{supersetData.position}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: colors.grey[400] }} />
          <Grid container spacing={2}>
            {supersetData.exercises.map((exerciseData) => {
              const { exercise, reps, sets, exerciseId, description } = exerciseData;
              return (
                <Grid item xs={12} md={6} key={exerciseId}>
                  <Card
                    sx={{
                      backgroundColor: colors.grey[800],
                      color: "#fff",
                      borderRadius: 2,
                      cursor: "pointer",
                      height: "100%",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                      },
                    }}
                    onClick={() => navigate(`/routines/${day}/${exerciseId}`)}
                  >
                    {exercise?.image ? (
                      <CardMedia
                        component="img"
                        height="120"
                        image={exercise.image}
                        alt={exercise.name}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          background: "linear-gradient(135deg, #1e293b, #334155)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="body1">{exercise?.name}</Typography>
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        {exercise?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        {exercise?.description}
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Series:</strong> {sets} &nbsp;&nbsp; <strong>Reps:</strong> {reps}
                      </Typography>
                      {description && (
                        <Typography
                          variant="body2"
                          mt={1}
                          sx={{ fontStyle: "italic" }}
                        >
                          {description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Topbar />
      <Box sx={{ padding: {md:"0 40px", xs:"0px 20px"}, mt: "30px" }}>
        <Grid container spacing={3}>
          {routines?.description?.[day]?.map((item, index) =>
            item.type === "single"
              ? renderSingleCard(item)
              : renderSupersetCard(item, index)
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default RoutineManagement;
